import type { ReactNode } from 'react';
import { CircleAlert, Inbox, Lock, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ApiError, errorMessage } from '@/lib/api';
import { Button } from './Button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-surface-hover', className)} aria-hidden />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink-subtle" role="status">
      <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden /> {label}…
    </div>
  );
}

export function EmptyState({ title, body, icon, action, className }: { title: string; body?: ReactNode; icon?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-hover text-ink-subtle">{icon ?? <Inbox className="h-5 w-5" />}</div>
      <p className="font-medium text-ink">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-ink-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PermissionDenied({ message }: { message?: string }) {
  return (
    <EmptyState
      icon={<Lock className="h-5 w-5" />}
      title="You don’t have access to this"
      body={message ?? 'Ask your administrator to grant the required permission.'}
    />
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && error.status === 403) return <PermissionDenied message={error.message} />;
  if (error instanceof ApiError && error.status === 404) return <EmptyState title="Not found" body="This record doesn’t exist or you don’t have access to it." />;
  return (
    <EmptyState
      icon={<CircleAlert className="h-5 w-5 text-danger" />}
      title="Couldn’t load this"
      body={
        <>
          {errorMessage(error)}
          {error instanceof ApiError && error.requestId && <span className="mt-1 block text-xs text-ink-subtle">Reference: {error.requestId}</span>}
        </>
      }
      action={onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>}
    />
  );
}
